import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { FiExternalLink } from 'react-icons/fi';

import { FilePicker } from '../../../components/Global/UploadMultiple/FilePicker';
import { getEvidenceUrl } from '../../../services/qualityControlService';
import { ActionRow } from './ActionsRepeater';

interface CompleteActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: ActionRow | null;
  isSubmitting: boolean;
  // Envia os arquivos novos (se houver) e conclui a ação.
  onSubmit: (files: File[], note: string) => void;
  // Tira um arquivo já enviado para esta ação (ainda pendente).
  onRemoveEvidence: (evidenceId: string) => void;
}

export const CompleteActionModal: React.FC<CompleteActionModalProps> = ({
  isOpen,
  onClose,
  action,
  isSubmitting,
  onSubmit,
  onRemoveEvidence,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setNote('');
    }
  }, [isOpen, action?.id]);

  const uploaded = action?.evidences || [];
  // A API exige ao menos uma evidência da execução anexada à ação.
  const canSubmit = files.length > 0 || uploaded.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent mx="4">
        <ModalHeader>Concluir ação</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.400">
            Ação
          </Text>
          <Text mb="4" wordBreak="break-word">
            {action?.description}
          </Text>

          <Text fontSize="sm" mb="1">
            O que foi feito (opcional)
          </Text>
          <Textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            maxLength={2000}
            placeholder="Ex.: lotes 1234 e 1235 reetiquetados e conferidos"
            mb="4"
          />

          <Text fontSize="sm" mb="1">
            Evidências da execução
          </Text>
          {uploaded.length > 0 && (
            <Flex direction="column" gridGap="2" mb="3">
              {uploaded.map(evidence => (
                <Flex
                  key={evidence.id}
                  justify="space-between"
                  align="center"
                  bg="gray.700"
                  borderRadius="md"
                  p="2"
                  gridGap="2"
                >
                  <Link
                    href={getEvidenceUrl(evidence.url)}
                    isExternal
                    fontSize="sm"
                    color="blue.300"
                    wordBreak="break-all"
                    display="flex"
                    alignItems="center"
                    gridGap="2"
                  >
                    <Icon as={FiExternalLink} flexShrink={0} />
                    {evidence.original_name}
                  </Link>
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="ghost"
                    isDisabled={isSubmitting}
                    onClick={() => onRemoveEvidence(evidence.id)}
                  >
                    Remover
                  </Button>
                </Flex>
              ))}
            </Flex>
          )}
          <FilePicker
            id="complete-action-evidence-picker"
            files={files}
            onChange={setFiles}
            isDisabled={isSubmitting}
          />
          <Box mt="2">
            <Text fontSize="xs" color="gray.400">
              Anexe ao menos uma foto ou PDF comprovando a execução. Depois de
              concluída, a ação não pode mais ser alterada.
            </Text>
          </Box>
        </ModalBody>
        <ModalFooter gridGap="3">
          <Button variant="outline" onClick={onClose} isDisabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            colorScheme="green"
            isDisabled={!canSubmit}
            isLoading={isSubmitting}
            onClick={() => onSubmit(files, note)}
          >
            Concluir ação
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
